import {useLayoutEffect, useRef, useState} from 'react';

const VIEWPORT_MARGIN = 8;

/**
 * Eat the next click anywhere in the document (capture phase, so React's
 * root listener never sees it). Disarms on the next mousedown too, so a
 * press that never produces a click can't leave it waiting for a later one.
 */
function swallowNextClick() {
  const swallow = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    disarm();
  };

  const disarm = () => {
    document.removeEventListener('click', swallow, true);
    document.removeEventListener('mousedown', disarm, true);
  };

  document.addEventListener('click', swallow, true);
  // Registered after the current mousedown has dispatched, so it only
  // catches the next press.
  setTimeout(() => {
    document.addEventListener('mousedown', disarm, true);
  });
}

type UseDismissablePortalMenuOptions = {
  isOpen: boolean;
  /** Must match the panel's rendered width so the right-edge clamp keeps it
   *  on screen. */
  menuWidth: number;
  /** Outside click or Escape — callers decide what "closed" means (e.g.
   *  `LibraryBreadcrumb` also returns focus to the trigger). */
  onDismiss: () => void;
  /** Swallow the click that follows a dismissing outside mousedown, so closing
   *  the menu over a clickable row doesn't also activate that row. */
  swallowDismissClick?: boolean;
};

/**
 * Shared positioning + dismissal for a portaled dropdown anchored to a
 * trigger element: clamps the panel to the viewport under the trigger, and
 * closes it on outside pointerdown or Escape. Both the trigger and the panel
 * live outside the portal's DOM subtree from React's perspective, so both
 * refs are checked before treating a click as "outside".
 *
 * Used by `OttoSessionMenu` and `LibraryBreadcrumb`'s per-segment sibling
 * menu — extract further behavior here rather than re-diverging it per call
 * site.
 */
export function useDismissablePortalMenu({
  isOpen,
  menuWidth,
  onDismiss,
  swallowDismissClick = false,
}: UseDismissablePortalMenuOptions) {
  const [position, setPosition] = useState({left: 0, top: 0});
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!isOpen) return;

    const reposition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const maxLeft = window.innerWidth - menuWidth - VIEWPORT_MARGIN;
      setPosition({
        left: Math.max(VIEWPORT_MARGIN, Math.min(rect.left, maxLeft)),
        top: rect.bottom + 6,
      });
    };

    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [isOpen, menuWidth]);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const inside =
        triggerRef.current?.contains(target) === true ||
        panelRef.current?.contains(target) === true;
      if (inside) return;
      if (swallowDismissClick) swallowNextClick();
      onDismiss();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onDismiss, swallowDismissClick]);

  return {position, triggerRef, panelRef};
}
